import { Toaster } from 'react-hot-toast';
import React, { useState } from 'react';
import { Outlet, Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuthStore } from '@/app/store/authStore';
import {
  HomeIcon,
  RssIcon,
  SettingsIcon,
  DownloadIcon,
  LogOutIcon,
  MenuIcon,
  XIcon,
  LayoutDashboardIcon,
  Building2,
  ListTodo,
  ShieldCheck,
  type LucideIcon,
} from 'lucide-react';
import { cn } from '@/shared/utils/cn';
import { Wordmark } from '@/shared/components/Wordmark';
import { ThemeToggle } from '@/shared/components/ThemeToggle';

interface NavItem {
  name: string;
  href: string;
  icon: LucideIcon;
}
interface NavSection {
  label: string;
  items: NavItem[];
}

export const Layout: React.FC = () => {
  const { user, logout, isAuthenticated } = useAuthStore();
  const navigate = useNavigate();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  if (!isAuthenticated) {
    return <Outlet />;
  }

  const sections: NavSection[] = [
    { label: 'Overview', items: [{ name: 'Dashboard', href: '/', icon: HomeIcon }] },
    {
      label: 'Intelligence',
      items: [
        { name: 'RSS Feeds', href: '/feeds', icon: RssIcon },
        { name: 'Fenergo Roadmap', href: '/productboard', icon: LayoutDashboardIcon },
        { name: 'Clients', href: '/clients', icon: Building2 },
      ],
    },
    {
      label: 'Workflow',
      items: [
        { name: 'Tasks', href: '/tasks', icon: ListTodo },
        { name: 'Export', href: '/export', icon: DownloadIcon },
      ],
    },
  ];

  const bottomItems: NavItem[] = [
    { name: 'Settings', href: '/settings', icon: SettingsIcon },
    ...(user?.isAdmin ? [{ name: 'Admin', href: '/admin', icon: ShieldCheck }] : []),
  ];

  const isCurrentPage = (href: string) =>
    href === '/' ? location.pathname === '/' : location.pathname.startsWith(href);

  const navLink = (item: NavItem, onClick?: () => void) => {
    const Icon = item.icon;
    const active = isCurrentPage(item.href);
    return (
      <Link
        key={item.name}
        to={item.href}
        onClick={onClick}
        aria-current={active ? 'page' : undefined}
        className={cn(
          'relative flex h-8 items-center gap-2.5 rounded-md pl-3 pr-2 text-sm font-medium transition-colors duration-150',
          active
            ? 'bg-muted text-foreground'
            : 'text-muted-foreground hover:bg-muted hover:text-foreground'
        )}
      >
        {active && (
          <span className="absolute left-0 top-1/2 h-4 w-0.5 -translate-y-1/2 rounded-full bg-primary" />
        )}
        <Icon className="h-4 w-4 shrink-0" />
        {item.name}
      </Link>
    );
  };

  const initials = (user?.email ?? '?').slice(0, 2).toUpperCase();

  const sidebarBody = (onNavigate?: () => void) => (
    <>
      <div className="flex h-14 shrink-0 items-center px-4">
        <Wordmark />
      </div>
      <nav className="flex-1 space-y-5 overflow-y-auto px-3 py-2">
        {sections.map((section) => (
          <div key={section.label} className="space-y-0.5">
            <p className="px-3 pb-1 text-[0.6875rem] font-semibold uppercase tracking-wider text-muted-foreground/70">
              {section.label}
            </p>
            {section.items.map((item) => navLink(item, onNavigate))}
          </div>
        ))}
      </nav>
      {/* Bottom: settings/admin + account */}
      <div className="shrink-0 space-y-0.5 border-t border-border px-3 py-3">
        {bottomItems.map((item) => navLink(item, onNavigate))}
        <div className="mt-2 flex items-center gap-2 rounded-md px-2 py-1.5">
          <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-primary/10 text-xs font-semibold text-primary">
            {initials}
          </span>
          <span className="min-w-0 flex-1 truncate text-xs text-muted-foreground" title={user?.email}>
            {user?.email}
          </span>
          <ThemeToggle />
          <button
            onClick={handleLogout}
            className="btn-icon h-8 w-8"
            title="Log out"
            aria-label="Log out"
          >
            <LogOutIcon className="h-4 w-4" />
          </button>
        </div>
      </div>
    </>
  );

  return (
    <div className="min-h-screen bg-background">
      {/* Mobile sidebar */}
      {sidebarOpen && (
        <div className="fixed inset-0 z-40 lg:hidden">
          <div
            className="fixed inset-0 bg-secondary-950/50 backdrop-blur-xs"
            onClick={() => setSidebarOpen(false)}
          />
          <div className="fixed inset-y-0 left-0 flex w-64 flex-col border-r border-border bg-surface">
            <button
              className="btn-icon absolute right-2 top-2"
              onClick={() => setSidebarOpen(false)}
              aria-label="Close menu"
            >
              <XIcon className="h-5 w-5" />
            </button>
            {sidebarBody(() => setSidebarOpen(false))}
          </div>
        </div>
      )}

      {/* Desktop sidebar */}
      <div className="hidden lg:fixed lg:inset-y-0 lg:flex lg:w-64 lg:flex-col">
        <div className="flex min-h-0 flex-1 flex-col border-r border-border bg-surface">
          {sidebarBody()}
        </div>
      </div>

      {/* Main content */}
      <div className="lg:pl-64">
        {/* Mobile top bar (desktop puts everything in the sidebar) */}
        <div className="sticky top-0 z-10 flex h-14 items-center gap-2 border-b border-border bg-surface/95 px-4 backdrop-blur lg:hidden">
          <button className="btn-icon" onClick={() => setSidebarOpen(true)} aria-label="Open menu">
            <MenuIcon className="h-5 w-5" />
          </button>
          <Wordmark showText={false} />
        </div>

        <Toaster
          position="top-right"
          toastOptions={{
            duration: 4000,
            style: {
              background: 'hsl(var(--overlay))',
              color: 'hsl(var(--foreground))',
              border: '1px solid hsl(var(--border))',
              boxShadow: '0 10px 30px rgb(15 23 42 / 0.12)',
              fontSize: '0.875rem',
            },
            success: { iconTheme: { primary: '#16a34a', secondary: '#fff' } },
            error: { duration: 5000, iconTheme: { primary: '#dc2626', secondary: '#fff' } },
          }}
        />

        <main className="min-h-[calc(100vh-3.5rem)] py-6 lg:min-h-screen">
          <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            <Outlet />
          </div>
        </main>
      </div>
    </div>
  );
};
